import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Monitor, FileText, LayoutGrid, ArrowRight, Plus, Sparkles } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { NewDesignButton, createBlankDesign } from "@/pages/Templates";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const headingFont = { fontFamily: "Outfit, sans-serif" };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ screens: 0, paired: 0, designs: 0, templates: 0 });

  useEffect(() => {
    Promise.all([api.get("/screens"), api.get("/designs"), api.get("/templates")])
      .then(([s, d, t]) => {
        const screens = s.data.screens || [];
        setStats({
          screens: screens.length,
          paired: screens.filter((x) => x.active_design_id || x.active_playlist_id).length,
          designs: (d.data.designs || []).length,
          templates: (t.data.templates || []).length,
        });
      })
      .catch(() => {});
  }, []);

  const cards = [
    { label: "Connected screens", value: stats.screens, icon: Monitor, to: "/dashboard/screens", testid: "stat-screens" },
    { label: "Live displays", value: stats.paired, icon: Monitor, to: "/dashboard/screens", testid: "stat-paired" },
    { label: "Your designs", value: stats.designs, icon: FileText, to: "/dashboard/designs", testid: "stat-designs" },
    { label: "Templates available", value: stats.templates, icon: LayoutGrid, to: "/dashboard/templates", testid: "stat-templates" },
  ];

  const daysLeft = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 md:p-10 max-w-7xl">
        {user?.is_trial && daysLeft !== null && (
          <div
            className="mb-8 rounded-2xl p-5 border bg-sky-50 border-sky-200 flex flex-wrap items-center justify-between gap-4"
            data-testid="trial-banner"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900" style={headingFont}>You&apos;re on the Pro trial</div>
                <div className="text-xs text-slate-600">{daysLeft} day{daysLeft === 1 ? "" : "s"} left · unlimited screens &amp; widgets</div>
              </div>
            </div>
            <Link to="/dashboard/billing">
              <Button size="sm" className="rounded-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="trial-cta">
                Upgrade or redeem code <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 font-semibold mb-2">Overview</div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05] font-bold text-slate-900" style={headingFont}>
              Welcome back{user?.name ? <>, <span className="text-sky-600">{user.name}</span></> : null}.
            </h1>
            <p className="mt-3 text-sm text-slate-600">Manage your screens and publish content from one place.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/dashboard/screens" data-testid="cta-pair-screen-link">
              <Button variant="outline" className="rounded-full border-slate-200 text-slate-700 hover:bg-slate-50 bg-white" data-testid="cta-pair-screen">
                <Plus className="h-4 w-4 mr-1.5" />Pair screen
              </Button>
            </Link>
            <NewDesignButton
              onPick={(o) => createBlankDesign(o, navigate)}
              variant="pill"
              label="+ New design"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
          {cards.map((c) => (
            <Link
              key={c.label}
              to={c.to}
              className="rounded-2xl p-4 sm:p-5 border border-slate-200 bg-white hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-sm group transition-all"
              data-testid={c.testid}
            >
              <div className="flex items-center justify-between mb-3 sm:mb-5">
                <c.icon className="h-4 w-4 text-sky-600" />
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-sky-600 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="text-3xl sm:text-5xl mb-1 tracking-tight text-slate-900 font-bold" style={headingFont}>{c.value}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium leading-tight">{c.label}</div>
            </Link>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <Link
            to="/dashboard/screens"
            className="rounded-3xl p-8 border border-sky-200 bg-gradient-to-br from-sky-50 to-white hover:shadow-md transition-all group"
            data-testid="card-screen-pair"
          >
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center mb-5">
              <Monitor className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold tracking-tight mb-2 text-slate-900" style={headingFont}>Pair a new screen</h3>
            <p className="text-sm text-slate-600 max-w-xs mb-5">Open Nexso on your TV, then enter the 6-digit code shown.</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700">Go to screens <ArrowRight className="h-4 w-4" /></span>
          </Link>
          <Link
            to="/dashboard/templates"
            className="rounded-3xl p-8 border border-slate-200 bg-white hover:shadow-md hover:border-sky-300 transition-all group"
            data-testid="card-template-pick"
          >
            <div className="h-12 w-12 rounded-2xl bg-sky-50 border border-sky-100 flex items-center justify-center mb-5">
              <LayoutGrid className="h-5 w-5 text-sky-600" />
            </div>
            <h3 className="text-2xl font-bold tracking-tight mb-2 text-slate-900" style={headingFont}>Start from a template</h3>
            <p className="text-sm text-slate-600 max-w-xs mb-5">350,000+ designs across menus, holidays, promos and industries.</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700">Browse templates <ArrowRight className="h-4 w-4" /></span>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
